import { defineStore } from 'pinia';
import { ref } from 'vue';

import { Result } from '@/classes/result';
import type { ActionPoint } from '@/types/actionPoint';
import type { Risk } from '@/types/risk';

import { ErrorMessage } from '@/messages/error';
import { useModuleStore } from '@/stores/module';
import type { ActionPointForm, ActionPointStatus, DetailedActionPoint, ImportOptions } from '@/types/actionPoint';
import type { Filter } from '@/types/filter';
import type { Note } from '@/types/note';
import type { UpdateActionPointsRequest } from '@/types/requests/updateActionPointsRequest';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';
import { getFilterQueries } from '@/utils/filter';
import toast, { showUntranslatedToast } from '@/utils/toast';
import { AxiosError } from 'axios';

export const useActionPointStore = defineStore('actionPoints', () => {
    const actionPoints = ref<DetailedActionPoint[]>([]);
    const moduleStore = useModuleStore();

    async function create(actionPoint: ActionPointForm): Promise<ActionPoint> {
        if (!actionPoint.module) actionPoint.module = moduleStore.defaultModule?._id;

        try {
            const result = await callApi({
                method: 'POST',
                endpoint: 'actionpoints',
                body: actionPoint
            });

            toast.success('ActionPointCreated');

            return result.object;
        } catch (error) {
            return handleAxiosError(error, [400], 'Actiepunt aanmaken is mislukt.');
        }
    }

    async function getDetailsById(id: string): Promise<DetailedActionPoint> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `actionpoints/${id}/details`
            });

            return result.object;
        } catch (error) {
            return handleAxiosError(error, [], 'Actiepunt details ophalen is mislukt.');
        }
    }

    async function setStatus(actionPointId: string, status: ActionPointStatus): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `actionpoints/${actionPointId}/status/${status}`
            });

            const index = actionPoints.value.findIndex((a) => a._id === actionPointId);
            if (index !== -1) if (actionPoints.value[index]) actionPoints.value[index]!.status = status;

            toast.success('ActionPointUpdated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [400], 'Actiepunt status kon niet worden gewijzigd.');
        }
    }

    async function update(actionPoint: ActionPoint): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                body: actionPoint,
                endpoint: `actionpoints/${actionPoint._id}`
            });

            toast.success('ActionPointUpdated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [400], ErrorMessage.ACTIONPOINT_UPDATE_FAILED);
        }
    }

    async function updateByIds(request: UpdateActionPointsRequest): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `actionpoints`,
                body: request
            });

            toast.success('ActionPointsUpdated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [400], 'Actiepunten konden niet opgeslagen worden.');
        }
    }

    async function createNote(actionpointId: string, note: Note, files: File[] = []): Promise<Result> {
        try {
            const formData = new FormData();
            formData.append('note', JSON.stringify(note));
            for (const file of files) {
                formData.append('files', file);
            }

            const result = await callApi({
                method: 'PUT',
                endpoint: `actionpoints/${actionpointId}/notes`,
                body: formData
            });
            toast.success('NoteCreated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [400], ErrorMessage.NOTE_CREATION_FAILED);
        }
    }

    async function deleteById(id: string): Promise<Result> {
        try {
            const result = await callApi({
                method: 'DELETE',
                endpoint: `actionpoints/${id}`
            });
            actionPoints.value = actionPoints.value.filter((a) => a._id !== id);
            toast.success('ActionPointDeleted');
            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Actiepunt kon niet verwijderd worden.');
        }
    }

    async function deleteByIds(ids: string[]): Promise<Result> {
        try {
            const result = await callApi({
                method: 'DELETE',
                endpoint: `actionpoints`,
                body: ids
            });

            actionPoints.value = actionPoints.value.filter((a) => !ids.some((id) => id === a._id));
            toast.success('ActionPointsDeleted');
            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Actiepunten konden niet verwijderd worden.');
        }
    }

    async function getDetailsFromOrganization(organizationId: string, filters?: Filter): Promise<Result> {
        try {
            const filterQueries = filters ? getFilterQueries(filters) : '';

            const result = await callApi({
                method: 'GET',
                endpoint: `actionpoints/organization/${organizationId}/details?${filterQueries}`
            });

            actionPoints.value = result.object;

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Kon actiepunten niet ophalen.');
        }
    }

    async function getChartData(organizationId: string, isCompleted: boolean, itemName: string): Promise<Result> {
        try {
            const completed = isCompleted ? 'completed' : 'incompleted';
            const result = await callApi({
                method: 'GET',
                endpoint: `actionpoints/${organizationId}/chartdata/${completed}/${itemName}`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Het ophalen van de grafiekdata van de actiepunten is mislukt.');
        }
    }

    async function getRisks(): Promise<Risk[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `risks`
            });

            return result.object as Risk[];
        } catch (error) {
            return handleAxiosError(error, [], 'Het ophalen van de risiconiveaus is niet gelukt.');
        }
    }

    async function getTimeLineChartData(organizationId: string, year: number): Promise<Result> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `actionpoints/${organizationId}/timeline/${year}`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Het ophalen van de tijdlijn data is mislukt.');
        }
    }

    async function importActionPoints(organizationId: string, file: File, options: ImportOptions): Promise<Result> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('options', JSON.stringify(options));

            const result = await callApi({
                method: 'POST',
                endpoint: `actionpoints/${organizationId}/import`,
                body: formData
            });

            toast.success('ActionPointsImported');

            return result;
        } catch (error) {
            if (error instanceof AxiosError && error.status == 404 && error.response?.data.message == ErrorMessage.SOURCE_NOT_FOUND) {
                const message = `Kon actiepunt(en) niet importeren. Bron "${error.response?.data.object}" bestaat nog niet.<br>Verander de bron naar een bestaande bron of voeg de bron toe via het beheermenu`;

                showUntranslatedToast('error', message, { duration: 12000 });
                return new Result(error.status, error.response.statusText);
            }

            const extraErrorInfo = error instanceof AxiosError && error.response?.data.message ? error.response?.data.message : '';
            return handleAxiosError(error, [], `Het importeren van (sommige) actiepunten is mislukt. ${extraErrorInfo}`);
        }
    }

    return {
        actionPoints,
        getTimeLineChartData,
        setStatus,
        create,
        createNote,
        update,
        updateByIds,
        deleteById,
        deleteByIds,
        getDetailsFromOrganization,
        getDetailsById,
        getChartData,
        getRisks,
        importActionPoints
    };
});
